/**
 * CHAARMANT CUSTOM: Variant Group Images — Standalone Script
 *
 * Filters product media gallery to show only images belonging to the selected
 * variant's group. Computes groups from consecutive image ordering in the
 * product media array (each variant's featured_media marks the start of its group).
 *
 * Key improvement over the theme's built-in approach: uses direct DOM manipulation
 * with a hidden pool + single reloadCells() call, instead of Flickity's remove/append
 * which triggers N relayouts and corrupts asNavFor sync.
 *
 * Self-initializing. Requires:
 *   - <product-info data-enable-variant-group-images data-product-url="...">
 *   - Flickity-based carousel (FoxTheme.Flickity)
 *
 * To migrate to a new theme version: copy this file + add the <script> include.
 * No patches to product-info.js or product-media.js required.
 */
(function () {
  "use strict";

  const FLICKITY_POLL_INTERVAL = 80;
  const FLICKITY_POLL_MAX = 50; // 80ms * 50 = 4s max wait
  const LOADING_TIMEOUT = 6000; // safety fallback to remove loading overlay

  function buildVariantGroupMapping(productData) {
    const variants = productData.variants;
    const media = productData.media;

    if (!media || !media.length || !variants || !variants.length) {
      return { enable: false, mapping: [] };
    }

    const seen = new Set();
    const starts = [];
    variants.forEach(function (v) {
      if (v.featured_media && !seen.has(v.featured_media.id)) {
        seen.add(v.featured_media.id);
        starts.push({ mediaId: v.featured_media.id, position: v.featured_media.position });
      }
    });

    if (starts.length === 0) return { enable: false, mapping: [] };

    starts.sort(function (a, b) { return a.position - b.position; });

    var mapping = [];
    starts.forEach(function (start, idx) {
      var startIdx = media.findIndex(function (m) { return m.id === start.mediaId; });
      if (startIdx === -1) return;
      var nextStart = starts[idx + 1];
      var endIdx = nextStart
        ? media.findIndex(function (m) { return m.id === nextStart.mediaId; })
        : media.length;
      var mediaIds = media
        .slice(startIdx, endIdx >= 0 ? endIdx : media.length)
        .map(function (m) { return String(m.id); });

      variants.forEach(function (v) {
        if (v.featured_media && v.featured_media.id === start.mediaId) {
          mapping.push({ id: v.id, media: mediaIds });
        }
      });
    });

    return { enable: mapping.length > 0, mapping: mapping };
  }

  function filterSlides(slides, mediaIds) {
    var matched = [];
    var rest = [];
    slides.forEach(function (slide) {
      if (mediaIds.includes(slide.dataset.mediaId)) {
        matched.push(slide);
      } else {
        rest.push(slide);
      }
    });
    // Sort matched by the order in mediaIds
    matched.sort(function (a, b) {
      return mediaIds.indexOf(a.dataset.mediaId) - mediaIds.indexOf(b.dataset.mediaId);
    });
    return { matched: matched, rest: rest };
  }

  function filterThumbnails(thumbs, mediaIds) {
    var matched = [];
    var rest = [];
    thumbs.forEach(function (thumb) {
      var mediaId = thumb.dataset.mediaId || thumb.querySelector("[data-media-id]")?.dataset.mediaId;
      if (mediaId && mediaIds.includes(mediaId)) {
        matched.push(thumb);
      } else {
        rest.push(thumb);
      }
    });
    matched.sort(function (a, b) {
      var aId = a.dataset.mediaId || a.querySelector("[data-media-id]")?.dataset.mediaId;
      var bId = b.dataset.mediaId || b.querySelector("[data-media-id]")?.dataset.mediaId;
      return mediaIds.indexOf(aId) - mediaIds.indexOf(bId);
    });
    return { matched: matched, rest: rest };
  }

  function applyCarouselFilter(flickityEl, flickity, allSlides, activeSlides, pool, cellSelector) {
    // Move ALL current slides to the pool (out of Flickity's view)
    var slider = flickityEl.querySelector(".flickity-slider");
    if (!slider) return;

    // Remove existing cells from Flickity's internal tracking without triggering relayouts
    // Move DOM nodes to pool
    allSlides.forEach(function (slide) {
      if (slide.parentNode) {
        pool.appendChild(slide);
      }
    });

    // Append only the active slides back into the slider
    activeSlides.forEach(function (slide, index) {
      var zoomButton = slide.querySelector(".js-photoswipe--zoom");
      if (zoomButton) {
        zoomButton.setAttribute("data-media-index", index);
      }
      slide.setAttribute("data-media-index", index);
      slide.setAttribute("data-index", index);
      slider.appendChild(slide);
    });

    // Single reloadCells call — Flickity rescans the slider container
    flickity.reloadCells();
    flickity.reposition();
    flickity.select(0, false, true);
  }

  function applyThumbnailFilter(thumbContainer, flickity, allThumbs, activeThumbs, pool) {
    var slider = thumbContainer.querySelector(".flickity-slider");
    // For non-flickity thumbnails (no wrapAround), the container IS the parent
    var parent = slider || thumbContainer;

    allThumbs.forEach(function (thumb) {
      if (thumb.parentNode) {
        pool.appendChild(thumb);
      }
    });

    activeThumbs.forEach(function (thumb, index) {
      thumb.setAttribute("data-thumbnail-index", index);
      thumb.setAttribute("data-index", index);
      parent.appendChild(thumb);
    });

    // Update transition classes based on count
    thumbContainer.classList.remove("disable-transition", "md:disable-transition");
    if (activeThumbs.length < 5) {
      thumbContainer.classList.add("disable-transition");
    }
    if (activeThumbs.length < 6) {
      thumbContainer.classList.add("md:disable-transition");
    }

    if (flickity) {
      flickity.option({ wrapAround: activeThumbs.length > 5 });
      flickity.reloadCells();
      flickity.reposition();
    }
  }

  function applyStackedFilter(mainMediaEl, allSlides, activeSlides) {
    mainMediaEl.innerHTML = "";
    activeSlides.forEach(function (item, index) {
      var zoomButton = item.querySelector(".js-photoswipe--zoom");
      if (zoomButton) {
        zoomButton.setAttribute("data-media-index", index);
      }
      item.setAttribute("data-media-index", index);
      item.setAttribute("data-index", index);
      mainMediaEl.appendChild(item);
    });
  }

  function initProductInfo(productInfo) {
    var productUrl = productInfo.dataset.productUrl;
    if (!productUrl) return;

    var mediaGallery = productInfo.querySelector("media-gallery");
    if (!mediaGallery) return;

    var mainMediaEl = mediaGallery.querySelector("[id^=Media-Gallery]");
    if (!mainMediaEl) return;

    // If the foxecom metafield already provides a valid mapping, let the theme handle it
    var scriptTag = productInfo.querySelector('#VariantGroupImages[type="application/json"]');
    if (scriptTag) {
      try {
        var metafieldData = JSON.parse(scriptTag.textContent);
        if (metafieldData && metafieldData.enable) return;
      } catch (e) { /* ignore parse errors, proceed with our logic */ }
    }

    // Safety timeout: remove loading overlay even if something fails
    var loadingTimeout = setTimeout(function () {
      mediaGallery.removeAttribute("data-media-loading");
    }, LOADING_TIMEOUT);

    // Fetch product data
    fetch(productUrl + ".js")
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(function (productData) {
        var mapping = buildVariantGroupMapping(productData);

        if (!mapping.enable) {
          clearTimeout(loadingTimeout);
          mediaGallery.removeAttribute("data-media-loading");
          return;
        }

        // State for this instance
        var state = {
          mapping: mapping,
          productData: productData,
          allMediaSlides: null,
          allThumbnails: null,
          mainPool: null,
          thumbPool: null,
          ready: false,
          lastMediaIds: null,
        };

        // Poll for Flickity readiness
        var pollCount = 0;
        var pollTimer = setInterval(function () {
          pollCount++;
          if (pollCount > FLICKITY_POLL_MAX) {
            clearInterval(pollTimer);
            clearTimeout(loadingTimeout);
            mediaGallery.removeAttribute("data-media-loading");
            return;
          }

          var flickity = window.FoxTheme && window.FoxTheme.Flickity
            ? window.FoxTheme.Flickity.data(mainMediaEl)
            : null;
          if (!flickity) return;

          clearInterval(pollTimer);

          // Cache all original media slides
          state.allMediaSlides = mediaGallery.domNodes && mediaGallery.domNodes.medias
            ? Array.from(mediaGallery.domNodes.medias)
            : Array.from(mainMediaEl.querySelectorAll(".f-product__media"));

          // Cache all thumbnails
          var thumbContainer = mediaGallery.querySelector(".f-product__media-thumbnails");
          state.allThumbnails = mediaGallery.domNodes && mediaGallery.domNodes.thumbnailItems
            ? Array.from(mediaGallery.domNodes.thumbnailItems)
            : (thumbContainer ? Array.from(thumbContainer.querySelectorAll(".f-product__media-thumbnails-item")) : []);

          // Create hidden pools (siblings of the media list, outside Flickity's view)
          state.mainPool = document.createElement("div");
          state.mainPool.style.display = "none";
          state.mainPool.setAttribute("aria-hidden", "true");
          mainMediaEl.parentNode.insertBefore(state.mainPool, mainMediaEl.nextSibling);

          if (thumbContainer) {
            state.thumbPool = document.createElement("div");
            state.thumbPool.style.display = "none";
            state.thumbPool.setAttribute("aria-hidden", "true");
            thumbContainer.parentNode.insertBefore(state.thumbPool, thumbContainer.nextSibling);
          }

          state.ready = true;

          // Monkey-patch updateMedia on the product-info instance
          productInfo.updateMedia = function (variant) {
            fastUpdateMedia(productInfo, mediaGallery, mainMediaEl, state, variant);
          };

          // Perform initial filter
          var currentVariant = productInfo.getSelectedVariant
            ? productInfo.getSelectedVariant(productInfo)
            : null;
          if (currentVariant) {
            fastUpdateMedia(productInfo, mediaGallery, mainMediaEl, state, currentVariant);
          }

          clearTimeout(loadingTimeout);
          mediaGallery.removeAttribute("data-media-loading");
        }, FLICKITY_POLL_INTERVAL);
      })
      .catch(function (err) {
        console.warn("[chaarmant-variant-group-images] Init failed:", err.message);
        clearTimeout(loadingTimeout);
        mediaGallery.removeAttribute("data-media-loading");
      });
  }

  function fastUpdateMedia(productInfo, mediaGallery, mainMediaEl, state, variant) {
    if (!variant || !state.ready) return;

    var groupImages = state.mapping.mapping.find(function (item) {
      return Number(item.id) === variant.id;
    });

    // Determine which media IDs to show
    var allMediaIds = state.allMediaSlides.map(function (s) { return s.dataset.mediaId; });
    var currentMediaIds = groupImages ? groupImages.media : allMediaIds;

    // Skip if already showing this set
    if (state.lastMediaIds && arraysEqual(state.lastMediaIds, currentMediaIds)) {
      // Still navigate to featured media
      if (variant.featured_media) {
        var flickity = window.FoxTheme.Flickity.data(mainMediaEl);
        if (flickity) {
          var idx = currentMediaIds.indexOf(String(variant.featured_media.id));
          if (idx >= 0) flickity.select(idx, false, true);
        }
      }
      return;
    }
    state.lastMediaIds = currentMediaIds;

    var isCarousel = !!mainMediaEl.querySelector(".flickity-slider");

    if (isCarousel) {
      var flickity = window.FoxTheme.Flickity.data(mainMediaEl);
      if (!flickity) return;

      var filtered = filterSlides(state.allMediaSlides, currentMediaIds);
      applyCarouselFilter(mainMediaEl, flickity, state.allMediaSlides, filtered.matched, state.mainPool, ".f-product__media");

      // Handle thumbnails
      var thumbContainer = mediaGallery.querySelector(".f-product__media-thumbnails");
      if (thumbContainer && state.allThumbnails.length > 0) {
        var thumbFlickity = window.FoxTheme.Flickity.data(thumbContainer);
        var filteredThumbs = filterThumbnails(state.allThumbnails, currentMediaIds);
        applyThumbnailFilter(thumbContainer, thumbFlickity, state.allThumbnails, filteredThumbs.matched, state.thumbPool);
      }
    } else {
      var filtered = filterSlides(state.allMediaSlides, currentMediaIds);
      applyStackedFilter(mainMediaEl, state.allMediaSlides, filtered.matched);
    }

    // Re-init Photoswipe if enabled
    if (mediaGallery.enableZoom) {
      if (mediaGallery.lightbox) mediaGallery.lightbox.destroy();
      if (typeof mediaGallery.initImageZoom === "function") {
        mediaGallery.initImageZoom();
      }
    }

    mediaGallery.removeAttribute("data-media-loading");
  }

  function arraysEqual(a, b) {
    if (a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  // Initialize all product-info elements with variant group images enabled
  var initialized = new WeakSet();

  function boot() {
    var elements = document.querySelectorAll("product-info[data-enable-variant-group-images]");
    elements.forEach(function (el) {
      if (!initialized.has(el)) {
        initialized.add(el);
        initProductInfo(el);
      }
    });
  }

  // Observe DOM for dynamically added product-info elements (e.g. quickview modals)
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches("product-info[data-enable-variant-group-images]")) {
          if (!initialized.has(node)) {
            initialized.add(node);
            initProductInfo(node);
          }
        }
        // Also check descendants (e.g. modal-dialog wrapping product-info)
        if (node.querySelectorAll) {
          var nested = node.querySelectorAll("product-info[data-enable-variant-group-images]");
          nested.forEach(function (el) {
            if (!initialized.has(el)) {
              initialized.add(el);
              initProductInfo(el);
            }
          });
        }
      }
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      boot();
      observer.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    boot();
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
